/* eslint-disable @typescript-eslint/no-misused-promises */
import { css } from '@emotion/react';
import {
  faArrowLeft,
  faArrowUpFromBracket,
  faCalendarMinus,
  faCalendarPlus
} from '@fortawesome/pro-regular-svg-icons';
import {
  ActionIcon,
  Box,
  Button,
  desktopFirstMediaQuery,
  Group,
  Icon,
  Tooltip,
  useShowNotification,
  useTheme
} from '@inflearn/ds-react';
import { navigate } from 'gatsby';
import { isObject } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useQueryClient } from 'react-query';
import useAddMySchedule from '../../../hooks/useAddMySchedule';
import useDeleteMySchedule from '../../../hooks/useDeleteMySchedule';
import useSession from '../../../hooks/useSession';
import { GATSBY_SITE_URL } from '../../../utils/env';
import { URLS } from '../../../utils/urls';
import { SESSION_SUB_TYPE } from '../commons/constants';
import LoginRequestModal from './LoginRequestModal';
import {
  GTMSessionAddClick,
  GTMSessionDelClick,
  GTMSessionDetailLinkShareClick
} from '../../../utils/GTM';

type Props = {
  sessionId: number;
  hasSchedule: boolean;
  subType: string;
  onlyAddButton?: boolean;
};

const SessionPageController = ({
  sessionId,
  hasSchedule: initialHasSchedule,
  subType,
  onlyAddButton = false
}: Props) => {
  const theme = useTheme();
  const { data: session, isError: isSessionError } = useSession();
  const { showNotification } = useShowNotification();
  const [isOpen, setIsOpen] = useState(false);
  const [hasSchedule, setHasSchedule] = useState(initialHasSchedule);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const { mutateAsync: addMySchedule, isLoading: isAddScheduleLoading } = useAddMySchedule();
  const { mutateAsync: deleteMySchedule, isLoading: isDeleteScheduleLoading } =
    useDeleteMySchedule();
  const queryClient = useQueryClient();
  const onClose = () => {
    setIsOpen(false);
  };
  const openModal = () => {
    setIsOpen(true);
  };
  const onClickMoveBeforePage = () => {
    if (typeof window === 'undefined') {
      return;
    }

    // history가 없는 경우 -> 직접 접근한 경우라서 세션 목록으로 이동
    if (window.history.length <= 1) {
      window.location.href = URLS.INFCON_2023__SESSION;
      return;
    }

    // referrer가 세션 상세 페이지인 경우 -> 로그인 redirect로 인한 history 꼬임 현상 -> 세션 목록으로 이동
    if (document.referrer.includes(`${sessionId}`)) {
      window.location.href = URLS.INFCON_2023__SESSION;
      return;
    }

    // referrer가 인프콘 페이지가 아닌 경우 -> 세션 목록으로 이동
    if (!document.referrer.includes(URLS.INFCON_2023__HOME)) {
      window.location.href = URLS.INFCON_2023__SESSION;
      return;
    }

    // referrer가 없는 경우 -> 직접 접근한 경우라서 세션 목록으로 이동
    if (document.referrer.length === 0) {
      window.location.href = URLS.INFCON_2023__SESSION;
      return;
    }

    void navigate(-1);
  };

  const onClickDeleteToMySchedule = useCallback(async () => {
    try {
      await deleteMySchedule(sessionId);

      await queryClient.invalidateQueries('MySchedule');

      showNotification({
        hasIcon: true,
        type: 'success',
        title: '내 일정에서 삭제되었습니다.'
      });

      setHasSchedule(false);
      GTMSessionDelClick();
    } catch (error) {
      showNotification({
        hasIcon: true,
        type: 'error',
        title: '일정 삭제중에 문제가 발생했습니다. 다시 시도해주세요.'
      });
    }
  }, [deleteMySchedule, queryClient, sessionId, showNotification]);

  const onClickAddToMySchedule = useCallback(async () => {
    if (!isObject(session) || isSessionError) {
      openModal();
      return;
    }

    try {
      await addMySchedule(sessionId);

      await queryClient.invalidateQueries('MySchedule');

      showNotification({
        hasIcon: true,
        type: 'success',
        title: '내 일정에 추가되었습니다.',
        actionButton: (
          <Button
            pos="relative"
            color={'dark.0'}
            style={{ color: `${theme.colors.dark[9]}` }}
            onClick={() => {
              void navigate(URLS.INFCON_2023__MY_NAVIGATE);
            }}>
            내 일정 보기
          </Button>
        )
      });

      setHasSchedule(true);

      GTMSessionAddClick();
    } catch (error) {
      showNotification({
        hasIcon: true,
        type: 'error',
        title: '일정 추가중에 문제가 발생했습니다. 다시 시도해주세요.'
      });
    }
  }, [addMySchedule, isSessionError, session, sessionId, showNotification]);

  const DesktopScheduleButton = useMemo(() => {
    if (subType === SESSION_SUB_TYPE.participation) {
      return <></>;
    }

    if (hasSchedule) {
      return (
        <Button
          leftIcon={<Icon icon={faCalendarMinus} />}
          color="blue"
          variant="outline"
          onClick={onClickDeleteToMySchedule}
          loading={isDeleteScheduleLoading}
          size="md">
          빼기
        </Button>
      );
    }

    return (
      <Button
        leftIcon={<Icon icon={faCalendarPlus} />}
        color="blue"
        variant="light"
        size="md"
        onClick={onClickAddToMySchedule}
        loading={isAddScheduleLoading}>
        추가
      </Button>
    );
  }, [
    subType,
    hasSchedule,
    onClickAddToMySchedule,
    isAddScheduleLoading,
    onClickDeleteToMySchedule,
    isDeleteScheduleLoading
  ]);

  const MobileScheduleButton = useMemo(() => {
    if (subType === SESSION_SUB_TYPE.participation) {
      return <></>;
    }

    if (hasSchedule) {
      return (
        <ActionIcon
          color="blue"
          variant="outline"
          onClick={onClickDeleteToMySchedule}
          loading={isDeleteScheduleLoading}
          aria-label="스케줄에서 삭제하기"
          size="lg">
          <Icon icon={faCalendarMinus} />
        </ActionIcon>
      );
    }

    return (
      <ActionIcon
        color="blue"
        variant="light"
        size="lg"
        onClick={onClickAddToMySchedule}
        loading={isAddScheduleLoading}
        aria-label="스케줄에 추가하기">
        <Icon icon={faCalendarPlus} />
      </ActionIcon>
    );
  }, [
    subType,
    hasSchedule,
    onClickAddToMySchedule,
    isAddScheduleLoading,
    onClickDeleteToMySchedule,
    isDeleteScheduleLoading
  ]);

  useEffect(() => {
    setHasSchedule(initialHasSchedule);
  }, [initialHasSchedule]);

  return (
    <Box
      component="section"
      css={[styleSessionDetailPageController, onlyAddButton && { marginBottom: 0 }]}>
      {!onlyAddButton && (
        <ActionIcon
          css={{
            ':hover': {
              backgroundColor: '#F1F3F5'
            }
          }}
          aria-label="이전 페이지로"
          size="lg"
          onClick={onClickMoveBeforePage}
          color="dark.6">
          <Icon icon={faArrowLeft} />
        </ActionIcon>
      )}
      <Group
        spacing={16}
        css={{
          [desktopFirstMediaQuery.mobile]: {
            display: 'none'
          }
        }}>
        {!onlyAddButton && (
          <CopyToClipboard
            text={`${GATSBY_SITE_URL}${URLS.INFCON_2023__SESSION_DETAIL_SHARE(sessionId)}`}
            onCopy={() => {
              setIsTooltipOpen(true);
              GTMSessionDetailLinkShareClick();
              setTimeout(() => {
                setIsTooltipOpen(false);
              }, 1500);
            }}>
            <Tooltip
              label="링크 복사 완료!"
              color="black"
              opened={isTooltipOpen}
              position="bottom"
              withArrow>
              <Button
                leftIcon={<Icon icon={faArrowUpFromBracket} />}
                variant="subtle"
                size="md"
                color="dark"
                css={{
                  ':hover': {
                    backgroundColor: '#F1F3F5'
                  }
                }}>
                공유
              </Button>
            </Tooltip>
          </CopyToClipboard>
        )}
        {DesktopScheduleButton}
      </Group>
      <Group
        spacing={16}
        css={{
          display: 'none',
          [desktopFirstMediaQuery.mobile]: {
            display: 'flex'
          }
        }}>
        {!onlyAddButton && (
          <CopyToClipboard
            text={`${GATSBY_SITE_URL}${URLS.INFCON_2023__SESSION_DETAIL_SHARE(sessionId)}`}
            onCopy={() => {
              setIsTooltipOpen(true);
              setTimeout(() => {
                setIsTooltipOpen(false);
              }, 1500);
            }}>
            <Tooltip
              label="링크 복사 완료!"
              color="black"
              opened={isTooltipOpen}
              position="bottom"
              withArrow>
              <ActionIcon
                variant="subtle"
                size="lg"
                color="dark"
                aria-label="공유하기"
                css={{
                  ':hover': {
                    backgroundColor: '#F1F3F5'
                  }
                }}>
                <Icon icon={faArrowUpFromBracket} />
              </ActionIcon>
            </Tooltip>
          </CopyToClipboard>
        )}
        {MobileScheduleButton}
      </Group>
      <LoginRequestModal isOpen={isOpen} onClose={onClose} />
    </Box>
  );
};

const styleSessionDetailPageController = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px',
  [desktopFirstMediaQuery.mobile]: {
    marginBottom: '16px'
  }
});

export default SessionPageController;
